import { createAsyncThunk } from '@reduxjs/toolkit';

import APIDataProvider, {
  TApiPaginationResponse,
  TApiResponse,
} from '../../../api/ApiDataProvider';
import { ApiPaths } from '../../../api/APIPaths';
import { ILessonStudentFeedback, ILessonStudentFeedbackRewardData } from '.';
import { ILessonHistory } from '../LessonHistorySlice';

export type TCreateLessonStudentFeedback = {
  tutors: string[];
  section: string;
  feedbackRewards?: string[];
  overallTutorRating?: number;
  teachingConcept?: number;
  questionPractice?: number;
  communicationSkills?: number;
  isContinueLesson?: 'Yes' | 'No';
  subjectAndSyllabusKnowledge?: number;
  punctualAndProfessional?: number;
  technicalSkills?: number;
  overallTutopiyaRating?: number;
  feedbackComments?: string;
  // new fields
  tutorExplanationClarity?: number;
  tutorSupportResponsiveness?: number;
  tutorAttitudeFriendliness?: number;
  classInteractionEngagement?: number;
  studentConfidenceImprovement?: number;
};

export type TUpdateLessonStudentFeedback = {
  id: string;
  feedbackRewards?: string[];
  overallTutorRating?: number;
  teachingConcept?: number;
  questionPractice?: number;
  communicationSkills?: number;
  isContinueLesson?: 'Yes' | 'No';
  subjectAndSyllabusKnowledge?: number;
  punctualAndProfessional?: number;
  technicalSkills?: number;
  overallTutopiyaRating?: number;
  feedbackComments?: string;
  // new fields
  tutorExplanationClarity?: number;
  tutorSupportResponsiveness?: number;
  tutorAttitudeFriendliness?: number;
  classInteractionEngagement?: number;
  studentConfidenceImprovement?: number;
};

export type TGetLessonStudentFeedbacks = {
  limit: number;
  offset: number;
  section?: string;
  studentId?: string;
  tutorId?: string;
};

const apiDataProvider = new APIDataProvider();

export const getLessonStudentFeedbackRewards = createAsyncThunk(
  '/lesson-student-feedbacks/feedback-rewards',
  async () => {
    const path = `${ApiPaths.lessonStudentFeedbacks}/feedback-rewards`;

    const response = await apiDataProvider.get<
      TApiResponse<ILessonStudentFeedbackRewardData>
    >(path);

    return response.data;
  }
);

export const createLessonStudentFeedback = createAsyncThunk(
  '/lesson-student-feedbacks/create',
  async (data: TCreateLessonStudentFeedback) => {
    const path = `${ApiPaths.lessonStudentFeedbacks}`;

    const response = await apiDataProvider.post<
      TApiResponse<ILessonStudentFeedback>
    >(path, data);

    return response.data;
  }
);

export const getLessonStudentFeedbacks = createAsyncThunk(
  '/lesson-student-feedbacks/list',
  async ({
    limit,
    offset,
    section,
    studentId,
    tutorId,
  }: TGetLessonStudentFeedbacks) => {
    let path = `${ApiPaths.lessonStudentFeedbacks}?limit=${limit}&offset=${offset}`;

    if (section) {
      path += `&section=${section}`;
    }

    if (studentId) {
      path += `&studentId=${studentId}`;
    }

    if (tutorId) {
      path += `&tutorId=${tutorId}`;
    }

    const response = await apiDataProvider.get<
      TApiResponse<TApiPaginationResponse<ILessonStudentFeedback>>
    >(path);

    return response.data;
  }
);

export const updateLessonStudentFeedbacks = createAsyncThunk(
  '/lesson-student-feedbacks/update-one',
  async (data: TUpdateLessonStudentFeedback) => {
    const path = `${ApiPaths.lessonStudentFeedbacks}/${data.id}`;

    const response = await apiDataProvider.put<
      TApiResponse<ILessonStudentFeedback>
    >(path, data);

    return response.data;
  }
);

export const getPendingLessonStudentFeedback = createAsyncThunk(
  '/lesson-student-feedbacks/pending-lessons',
  async () => {
    const path = `${ApiPaths.lessonStudentFeedbacks}/pending-feedback`;

    const response = await apiDataProvider.get<TApiResponse<ILessonHistory>>(
      path
    );

    return response.data;
  }
);
